/**
 * Graphics quality presets.
 *
 * Rule from the brief: visual settings may never change what the player can
 * *read*. Projectile size, colour and the attack warning are identical at every
 * preset; only the decorative budget moves.
 */
import { create } from 'zustand';

export type QualityPreset = 'low' | 'medium' | 'high';

export interface QualityProfile {
  dprCap: number;
  shadows: boolean;
  shadowMapSize: number;
  /** Extra candle point-lights beyond the two key lights. */
  candleLights: number;
  bloom: boolean;
  bloomIntensity: number;
  ssao: boolean;
  depthOfField: boolean;
  chromaticAberration: boolean;
  dustParticles: number;
  /** Segment counts for procedural geometry. */
  geometryDetail: number;
  /** Cap on simultaneously animating impact effects. */
  maxImpacts: number;
  reflections: boolean;
}

export const QUALITY: Record<QualityPreset, QualityProfile> = {
  low: {
    dprCap: 1,
    shadows: false,
    shadowMapSize: 512,
    candleLights: 0,
    bloom: true,
    bloomIntensity: 0.5,
    ssao: false,
    depthOfField: false,
    chromaticAberration: false,
    dustParticles: 0,
    geometryDetail: 8,
    maxImpacts: 6,
    reflections: false,
  },
  medium: {
    dprCap: 1.5,
    shadows: true,
    shadowMapSize: 1024,
    candleLights: 2,
    bloom: true,
    bloomIntensity: 0.75,
    ssao: false,
    depthOfField: false,
    chromaticAberration: true,
    dustParticles: 320,
    geometryDetail: 16,
    maxImpacts: 12,
    reflections: true,
  },
  high: {
    dprCap: 2,
    shadows: true,
    shadowMapSize: 2048,
    candleLights: 4,
    bloom: true,
    bloomIntensity: 0.95,
    ssao: true,
    depthOfField: true,
    chromaticAberration: true,
    dustParticles: 700,
    geometryDetail: 24,
    maxImpacts: 20,
    reflections: true,
  },
};

const KEY = 'spell.settings.v1';

interface Persisted {
  preset: QualityPreset;
  masterVolume: number;
  showDebug: boolean;
  invertY: boolean;
  sensitivity: number;
}

function load(): Persisted {
  const fallback: Persisted = {
    preset: 'high',
    masterVolume: 0.75,
    showDebug: false,
    invertY: false,
    sensitivity: 1,
  };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<Persisted>) };
  } catch {
    return fallback;
  }
}

export interface SettingsState extends Persisted {
  quality: QualityProfile;
  /**
   * True when WebGL is being served by a software rasteriser (SwiftShader,
   * llvmpipe, a VM). Post-processing and shadows are the first things a
   * software path cannot afford, so they are dropped outright rather than
   * letting the frame budget collapse.
   */
  softwareRenderer: boolean;
  setSoftwareRenderer: (on: boolean) => void;
  /** Live frames-per-second, updated by the render loop (never re-renders UI). */
  setPreset: (preset: QualityPreset) => void;
  setMasterVolume: (v: number) => void;
  setShowDebug: (v: boolean) => void;
  setInvertY: (v: boolean) => void;
  setSensitivity: (v: number) => void;
}

const initial = load();

export const useSettings = create<SettingsState>((set, get) => ({
  ...initial,
  quality: QUALITY[initial.preset],
  softwareRenderer: false,
  setSoftwareRenderer: (softwareRenderer) => {
    if (get().softwareRenderer === softwareRenderer) return;
    set({ softwareRenderer });
    if (softwareRenderer) set({ preset: 'low', quality: QUALITY.low });
  },
  setPreset: (preset) => {
    set({ preset, quality: QUALITY[preset] });
    persist(get());
  },
  setMasterVolume: (masterVolume) => {
    set({ masterVolume });
    persist(get());
  },
  setShowDebug: (showDebug) => {
    set({ showDebug });
    persist(get());
  },
  setInvertY: (invertY) => {
    set({ invertY });
    persist(get());
  },
  setSensitivity: (sensitivity) => {
    set({ sensitivity });
    persist(get());
  },
}));

function persist(state: SettingsState) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        preset: state.preset,
        masterVolume: state.masterVolume,
        showDebug: state.showDebug,
        invertY: state.invertY,
        sensitivity: state.sensitivity,
      } satisfies Persisted),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Auto-downgrade when the frame budget is repeatedly missed. Frame stability
 * outranks visual quality — the brief is explicit about this.
 */
export function considerAutoDowngrade(avgFps: number) {
  const { preset, setPreset } = useSettings.getState();
  if (avgFps >= 32) return;
  if (preset === 'high') setPreset('medium');
  else if (preset === 'medium') setPreset('low');
}
